import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { MouseEvent, useMemo, useRef, useState } from "react";
import GetAppIcon from '@mui/icons-material/GetApp';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import RecommendIcon from '@mui/icons-material/Recommend';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useSelector } from "react-redux";
import { selectSelectedDatabase, selectSelectedStudy } from "../../common/databaseSlice";
import { SalesforceIssue, SalesforceIssueRecord, useGetSalesforceIssuesQuery } from "../../../api/issueApiSlice";
import { useGetDatabaseVersionQuery } from "../../../api/databaseApiSlice";
import { useUserInfoQuery } from "../../../api/userApiSlice";
import { MainDomainEnum } from "../dashboardTypes";

export const CHART_HEIGHT = 350;
export const AXIS_LABEL_FONT_SIZE = 12;
export const MAX_LABEL_TRUNCATE = 13;
export const CHART_PADDING = { top: 30, right: 30, bottom: 30, left: 35 };
export const TOOLTIP_PADDING = "padding:8px 12px";
export const Y_AXIS_SEGMENTS = 10;       // divide chart into this many steps
export const Y_AXIS_STEP_ROUNDING = 50;
export const TABLE_ROW_HEIGHT = 42;
export const TABLE_HEADER_HEIGHT = 48;
const TABLE_BORDER_HEIGHT = 2;

export const estimateTableHeight = (rowCount: number) =>
    TABLE_HEADER_HEIGHT + rowCount * TABLE_ROW_HEIGHT + TABLE_BORDER_HEIGHT;

const kcUrl = window.env.keycloak.url;
const realm = window.env.keycloak.realm;

export const truncateLabel = (s: string, maxLength = MAX_LABEL_TRUNCATE) =>
    s.length > maxLength ? `${s.slice(0, maxLength)}…` : s;

type Props = {
    title: string;
    subtitle?: string;
    table_name?: string;
    breakdownAvailable?: boolean;
    breakdownClicked?: (field: string) => void;
    ddAvailable: boolean;
    imagingData: any;
    eegData?: any;
    children: any;
    showIssueOptions?: boolean;
    salesforceRecordsByInstrument?: Record<string, SalesforceIssueRecord[]>;
    instrumentDomainRelation?: Record<string, string>;
    tableRows?: Record<string, any>[];
}

const triggerFileDownload = (href: string, filename: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const toSafeFilename = (name: string) =>
    name.replace(/[^\w\-. ]+/g, '_').trim().replace(/\s+/g, '_') || 'chart';

const toCsv = (rows: Record<string, any>[]) => {
    const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
    const escapeCell = (value: unknown) => {
        const text = String(value ?? '');
        return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    return [
        headers.map(escapeCell).join(','),
        ...rows.map((row) => headers.map((h) => escapeCell(row[h])).join(','))
    ].join('\n');
};

const ChartPaper = (props: Props) => {
    const {
        title,
        subtitle,
        table_name,
        breakdownAvailable,
        breakdownClicked,
        ddAvailable,
        imagingData,
        eegData,
        children,
        showIssueOptions = false,
        salesforceRecordsByInstrument,
        instrumentDomainRelation,
        tableRows
    } = props;

    const contentRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const database = useSelector(selectSelectedDatabase);
    const selectedStudy = useSelector(selectSelectedStudy);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [issuesDialogOpen, setIssuesDialogOpen] = useState(false);
    const [currentInstrument, setCurrentInstrument] = useState('');

    // Query for issues data when dialog is open
    const { data: issuesData, isLoading: isLoadingIssues, error: issuesError } = useGetSalesforceIssuesQuery(
        { instrument: currentInstrument, domain: database },
        { skip: !issuesDialogOpen }
    );
    const { data: version } = useGetDatabaseVersionQuery({ database });
    const { data: userData } = useUserInfoQuery({});

    const eegLabels = useMemo(() => {
        return Object.keys(eegData || {}).reduce((acc: Record<string, string>, key) => {
            const title = eegData?.[key]?.title;
            if (title){
                acc[title] = key;
            }
            return acc;
        }, {});
    }, [eegData]);

    const showReportOptions = useMemo(() => {
        const locationPathNormalized = location.pathname.slice(1);
        return locationPathNormalized.split('/').length === 1 && location.pathname.includes('/by-domain');
    }, [location]);

    // Extract instrument from URL pathname
    const getInstrument = () => {
        const pathname = location.pathname.replace(/\/\s*$/, '');
        const paths = pathname.split('/');
        return paths[paths.length - 1];
    };

    const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        setAnchorEl(event.currentTarget as HTMLElement);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleIssuesDialogClose = () => {
        setIssuesDialogOpen(false);
    };

    const handleDownload = () => {
        const filename = toSafeFilename(subtitle || title);
        const canvases = Array.from(contentRef.current?.querySelectorAll('canvas') ?? []);

        if (canvases.length > 0) {
            // Charts are rendered on canvases (a histogram can have a second one for its missing values);
            // place them side by side on white so transparent areas aren't black in the PNG
            const output = document.createElement('canvas');
            output.width = canvases.reduce((total, canvas) => total + canvas.width, 0);
            output.height = Math.max(...canvases.map((canvas) => canvas.height));
            const ctx = output.getContext('2d');
            if (ctx) {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, output.width, output.height);
                let offset = 0;
                canvases.forEach((canvas) => {
                    ctx.drawImage(canvas, offset, 0);
                    offset += canvas.width;
                });
                triggerFileDownload(output.toDataURL('image/png'), `${filename}.png`);
            }
        } else if (tableRows && tableRows.length > 0) {
            const blob = new Blob([toCsv(tableRows)], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            triggerFileDownload(url, `${filename}.csv`);
            URL.revokeObjectURL(url);
        }
        handleClose();
    };

    const handleReportIssue = () => {
        // Extract instrument from URL pathname
        const pathname = location.pathname.replace(/\/\s*$/, '');
        const paths = pathname.split('/');
        const instrument = paths[paths.length - 1];

        // Construct the URL with the required parameters
        const variableLabel = subtitle || title;
        const salesforceUrl = `${window.env.salesforcePortalURL}redirect?reportpage=reportissue&domain=${database}&VariableLabel=${encodeURIComponent(variableLabel)}&Instrument=${instrument}&issuer=${kcUrl}realms/${realm}&study=${window.env.salesforceStudy}`;
        // Open the URL in a new tab
        window.open(salesforceUrl, '_blank');

        handleClose();
    };

    const handleViewReportedIssues = () => {
        // Get the instrument from the URL pathname
        const instrument = getInstrument();

        // Set the current instrument and open the dialog
        setCurrentInstrument(instrument);
        setIssuesDialogOpen(true);

        handleClose();
    };

    const handleApproval = (isInstrument: boolean) => {
        if (!table_name || !version) return;

        const domain = instrumentDomainRelation ? instrumentDomainRelation[table_name] : MainDomainEnum.ALL_DOMAINS;
        const username = userData?.username || userData?.email || "unknown";

        const issueType = isInstrument ? 'DATA_APPROVED' : 'DATA_DICTIONARY_APPROVED';
        let existingApprovalId: string | undefined;
        
        if (table_name && salesforceRecordsByInstrument && salesforceRecordsByInstrument[table_name]) {
            const instrumentRecords = salesforceRecordsByInstrument[table_name];
            const existingRecord = instrumentRecords.find(record => record.issueType === issueType);

            if (existingRecord) {
                existingApprovalId = existingRecord.id;
            }
        }

        const defaultURL = new URL(`${window.env.salesforcePortalURL}redirect`);
        defaultURL.searchParams.set('page', isInstrument ? 'dataapprovedrelease' : 'datadictionaryapprovedrelease');
        defaultURL.searchParams.set('datasetVersion', version);
        defaultURL.searchParams.set('researchDomain', domain);
        defaultURL.searchParams.set('instrument', table_name);
        defaultURL.searchParams.set('issueType', issueType);
        defaultURL.searchParams.set('study', window.env.salesforceStudy);
        
        if (existingApprovalId) {
            defaultURL.searchParams.set('approvalId', existingApprovalId);
        }

        if (window.location.host.includes('localhost')) {
            defaultURL.searchParams.set('username', 'testsubjectaut')
            defaultURL.searchParams.set('datasetReleaseType', 'ONGOING-QC');
            defaultURL.searchParams.set("issuer", `https://authid.lassoinformatics.com/realms/u-nbdchbcdbeta-a-s-e1-ek-kc`);
        } else {
            defaultURL.searchParams.set('username', username)
            defaultURL.searchParams.set('datasetReleaseType', selectedStudy);
            defaultURL.searchParams.set("issuer", `${kcUrl}realms/${realm}`);
        }

        console.log('salesforceUrl: ', defaultURL.toString());
        window.open( defaultURL.toString(), '_blank');
        handleClose();
    }

    const onClick = (() => {
        if (Object.keys(eegLabels).includes(title)) {
            navigate('/data-wrangler', { state: { dictSearch: eegLabels[title] } })
        } else {
            navigate('/data-wrangler', { state: { dictSearch: title } })
        }
    })

    const onVariableClick = (() => {
        if (table_name) {
            navigate(`/by-domain/${encodeURIComponent(table_name)}`, { state: { previousLocation: location } });
        }
    });

    const onBreakdownClicked = (() => {
        if (breakdownClicked) {
            breakdownClicked(subtitle ?? '')
        }
    })

    const DataApprovalItems = () => {
        if (!table_name || !salesforceRecordsByInstrument || !salesforceRecordsByInstrument[table_name] || salesforceRecordsByInstrument[table_name]?.length === 0) return null;

        const instrumentRecords = salesforceRecordsByInstrument[table_name];
        const hasDataApprovalRecord = instrumentRecords?.some(record => record.issueType === "DATA_APPROVED" && record.approved === 'Yes')
        const hasDataDictionaryApprovalRecord = instrumentRecords?.some(record => record.issueType === "DATA_DICTIONARY_APPROVED" && record.approved === 'Yes')

        return (
            <>
                {hasDataApprovalRecord && (
                    <Tooltip title="This instrument has been approved">
                        <RecommendIcon color="success"/>
                    </Tooltip>
                )}
                {hasDataDictionaryApprovalRecord && (
                    <Tooltip title="This instrument's data dictionary has been approved">
                        <MenuBookIcon color="success"/>
                    </Tooltip>
                )}
            </>
        )
    }

    return (
        <Paper
            sx={{
                padding: "20px",
                marginBottom: "16px",
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
            }}
            onContextMenu={handleContextMenu}
        >
            <Stack gap={3} flexGrow={1}>
                <Tooltip title={title} followCursor>
                    <Typography
                        variant={"h5"}
                        fontWeight={"bold"}
                        noWrap={true}
                    >
                        {title}
                    </Typography>
                </Tooltip>
                {subtitle && (
                    <Tooltip title={subtitle} followCursor>
                        <Typography noWrap={true}>{subtitle}</Typography>
                    </Tooltip>
                )}
                <div ref={contentRef} style={{ display: 'contents' }}>
                    {children}
                </div>
            </Stack>

            <Stack direction="row" alignItems="center">
                <DataApprovalItems />

                {ddAvailable && !Object.keys(imagingData).includes(title) && (

                    <>
                        <Button
                            sx={{ margin: 2 }}
                            variant="outlined"
                            onClick={onClick}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            View Data Dictionary
                        </Button>
                        <Button
                            sx={{ margin: 2 }}
                            variant="outlined"
                            onClick={onVariableClick}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            View Variable Breakdown
                        </Button>
                    </>
                )}
                {breakdownAvailable && (
                    <Button
                        sx={{ margin: 2 }}
                        variant="outlined"
                        onClick={onBreakdownClicked}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        View By Site/Visit Breakdown
                    </Button>
                )}
            </Stack>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <MenuItem onClick={handleDownload} sx={{ gap: 1 }}>
                    <GetAppIcon fontSize="small" />
                    Download
                </MenuItem>
                {showReportOptions && (
                    <MenuItem onClick={() => handleApproval(true)} sx={{ gap: 1 }}>
                        Approve data for release
                    </MenuItem>
                )}
                {showReportOptions && (
                    <MenuItem onClick={() => handleApproval(false)} sx={{ gap: 1 }}>
                        Approve data dictionary for release
                    </MenuItem>
                )}

                {showIssueOptions && (
                    <MenuItem onClick={handleReportIssue} sx={{ gap: 1 }}>
                        <ReportProblemIcon fontSize="small" />
                        Report an Issue
                    </MenuItem>
                )}
                {showIssueOptions && (
                    <MenuItem onClick={handleViewReportedIssues} sx={{ gap: 1 }}>
                        <VisibilityIcon fontSize="small" />
                        View Reported Issues
                    </MenuItem>
                )}
            </Menu>

            {/* Dialog for displaying reported issues */}
            <Dialog
                open={issuesDialogOpen}
                onClose={handleIssuesDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Reported Issues for {currentInstrument}
                    <Button 
                        onClick={handleIssuesDialogClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </Button>
                </DialogTitle>
                <DialogContent>
                    {isLoadingIssues && (
                        <Stack alignItems="center" justifyContent="center" padding={4}>
                            <CircularProgress />
                        </Stack>
                    )}

                    {issuesError && (
                        <Typography color="error" padding={2}>
                            Error loading issues. Please try again.
                        </Typography>
                    )}

                    {issuesData && issuesData.length === 0 && (
                        <Typography padding={2}>
                            No issues found for this instrument.
                        </Typography>
                    )}

                    {issuesData && issuesData.length > 0 && (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Variable Label</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Reported By</TableCell>
                                        <TableCell>Instrument</TableCell>
                                        <TableCell>Study</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {issuesData.map((issue: SalesforceIssue) => (
                                        <TableRow key={issue.id}>
                                            <TableCell>{issue.variableLabel}</TableCell>
                                            <TableCell>{issue.description}</TableCell>
                                            <TableCell>{issue.username}</TableCell>
                                            <TableCell>{issue.instrument}</TableCell>
                                            <TableCell>{issue.study}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleIssuesDialogClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default ChartPaper;
